/**
 * LevelStore — 关卡持久化抽象层
 *
 * ADR-003/ V14：DevLevelStore（开发 middleware） + StaticBuiltinLevelStore（公共内容）
 * + AccountUserLevelStore（生产账号 API）。上层代码不感知存储后端。
 *
 * 关键设计：
 * - LevelStore 只存地图+AI，不存玩家代码（玩家代码走独立 commit 存储）
 * - .level.zip 导出格式：manifest.json + map.json + ai.py（不含 player_template.py）
 * - _builtin 目录只读，不可通过 API 修改/删除
 * - middleware 只在 Vite dev server 可用；生产用户关卡通过 JWT 账号 API 存储
 */

import { zipSync, strToU8, unzipSync, strFromU8 } from 'fflate';
import { CHAR_TO_NUM } from '../engine/tile-codec';
import { AuthRequiredError, getLevelStorageNamespace, isLoggedIn } from '../core/user-context';
import { getAuthToken } from '../services/llm-service';

// ============================================================
// 类型定义
// ============================================================

/** 关卡元数据（与 manifest.json schema 对齐） */
export interface LevelMeta {
  levelId: string;
  name: string;
  difficulty: 'tutorial' | 'easy' | 'medium' | 'hard';
  mapSize: number;
  aiName: string;
  description: string;
  isBuiltin: boolean;
  createdAt?: string;
  thumbnailSrc?: string;
  // A4: 以下为 manifest.json 中存在但此前未映射的字段（全部 optional，向后兼容）
  difficultyStars?: number;
  apiVersion?: string;
  maxTicks?: number;
  characterId?: string;
  tags?: string[];
  version?: string;
  // ★ mini-map 渲染用：list() 接口会附带 map 数据，屏 1 卡片画真实 tile
  //   load() 接口走 LevelData.map（更完整），这里 optional 防止破坏旧调用方
  map?: MapData;
  /** 队伍规模（1v1/2v2/3v3），从 map.json constraints.max_units_per_team 提取，默认 1 */
  maxUnitsPerTeam?: number;
}

/** 地图约束（map.json constraints 字段） */
export interface MapConstraints {
  symmetric?: boolean;
  max_units_per_team?: number;  // 1=1v1, 2=2v2, 3=3v3, 默认 1
}

/** 地图数据（map.json 内容） */
export interface MapData {
  width: number;
  height: number;
  tiles: number[][];        // 0=empty, 1=cover, 2=obstacle, 3=unreachable
  spawnA: [number, number];
  spawnB: [number, number];
  constraints?: MapConstraints;
}

/** 完整关卡数据（加载/保存时的完整结构） */
export interface LevelData extends LevelMeta {
  map: MapData;
  playerTemplate: string;   // player_template.py 源码
  aiCode: string;           // ai.py 源码
}

// ============================================================
// LevelStore 接口
// ============================================================

export interface LevelStore {
  /** 保存关卡（创建或覆盖） */
  save(level: LevelData): Promise<void>;

  /** 加载单个关卡（含所有文件） */
  load(levelId: string): Promise<LevelData>;

  /** 列出所有可用关卡 */
  list(): Promise<LevelMeta[]>;

  /** 删除关卡（仅允许 user-created / imported 类型） */
  delete(levelId: string): Promise<void>;

  /** 导出关卡为 .level.zip Blob */
  exportZip(levelId: string): Promise<Blob>;

  /** 从 .level.zip 导入关卡，返回新 levelId */
  importZip(zipBlob: Blob): Promise<LevelData>;
}

// ============================================================
// 工具函数：.level.zip 打包/解包（fflate）
// ============================================================

/**
 * 将 LevelData 打包为 .level.zip Blob
 * 内容：manifest.json + map.json + ai.py（不含 player_template.py）
 */
export function packLevelZip(level: LevelData): Blob {
  const manifest: Record<string, unknown> = {
    level_id: level.levelId,
    name: level.name,
    difficulty: level.difficulty,
    mapSize: level.mapSize,
    aiName: level.aiName,
    description: level.description,
    isBuiltin: level.isBuiltin,
    createdAt: level.createdAt ?? new Date().toISOString(),
    // A4: round-trip 保留新字段
    ...(level.difficultyStars !== undefined ? { difficultyStars: level.difficultyStars } : {}),
    ...(level.apiVersion !== undefined ? { apiVersion: level.apiVersion } : {}),
    ...(level.maxTicks !== undefined ? { maxTicks: level.maxTicks } : {}),
    ...(level.characterId !== undefined ? { characterId: level.characterId } : {}),
    ...(level.tags !== undefined ? { tags: level.tags } : {}),
    ...(level.version !== undefined ? { version: level.version } : {}),
  };

  const files: Record<string, Uint8Array> = {
    'manifest.json': strToU8(JSON.stringify(manifest, null, 2)),
    'map.json': strToU8(JSON.stringify(level.map, null, 2)),
    'ai.py': strToU8(level.aiCode),
  };

  const zipped = zipSync(files);
  return new Blob([zipped], { type: 'application/zip' });
}

/**
 * 从 .level.zip Blob 解包为 LevelData
 * 验证必需文件存在，缺失则抛错
 */
export async function unpackLevelZip(zipBlob: Blob): Promise<LevelData> {
  const buf = new Uint8Array(await zipBlob.arrayBuffer());
  const files = unzipSync(buf);

  // 验证必需文件
  if (!files['manifest.json']) throw new Error('Invalid .level.zip: missing manifest.json');
  if (!files['map.json']) throw new Error('Invalid .level.zip: missing map.json');
  if (!files['ai.py']) throw new Error('Invalid .level.zip: missing ai.py');

  const manifest = JSON.parse(strFromU8(files['manifest.json']));
  const map = JSON.parse(strFromU8(files['map.json']));
  const aiCode = strFromU8(files['ai.py']);

  const level: LevelData = {
    levelId: manifest.level_id ?? `imported_${Date.now().toString(36)}`,
    name: manifest.name ?? 'Imported Level',
    difficulty: manifest.difficulty ?? 'easy',
    mapSize: manifest.mapSize ?? map.width ?? 25,
    aiName: manifest.aiName ?? 'Imported AI',
    description: manifest.description ?? '',
    isBuiltin: false,
    createdAt: manifest.createdAt ?? new Date().toISOString(),
    // A4: round-trip 保留新字段
    difficultyStars: manifest.difficultyStars,
    apiVersion: manifest.apiVersion,
    maxTicks: manifest.maxTicks,
    characterId: manifest.characterId,
    tags: manifest.tags,
    version: manifest.version,
    map,
    playerTemplate: '',
    aiCode,
  };

  return level;
}

/**
 * 触发浏览器下载 .level.zip
 */
export function downloadLevelZip(levelId: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${levelId}.level.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================
// StaticBuiltinLevelStore — 生产环境静态官方关卡
// ============================================================

interface BuiltinLevelIndexEntry {
  levelId: string;
  path: string;
}

interface BuiltinLevelIndex {
  version: number;
  levels: BuiltinLevelIndexEntry[];
}

export function normalizeMap(raw: Record<string, unknown>): MapData {
  const hasStandardTiles = Array.isArray(raw.tiles);
  const rawTiles = hasStandardTiles ? raw.tiles : raw.terrain_grid;
  const isLegacyTerrainGrid = !hasStandardTiles && Array.isArray(raw.terrain_grid);
  const tiles: number[][] = Array.isArray(rawTiles)
    ? rawTiles.map((row) => {
        if (typeof row === 'string') return Array.from(row).map((ch) => CHAR_TO_NUM[ch] ?? 0);
        if (Array.isArray(row)) {
          return row.map((value) => {
            const numericValue = Number(value) || 0;
            // v5 legacy terrain_grid: 1=obstacle, 2=cover；LevelStore: 1=cover, 2=obstacle。
            if (isLegacyTerrainGrid && numericValue === 1) return 2;
            if (isLegacyTerrainGrid && numericValue === 2) return 1;
            return numericValue;
          });
        }
        return [];
      })
    : [];

  const dimensions = raw.dimensions as { width?: number; height?: number } | undefined;
  const legacySize = Array.isArray(raw.size) ? raw.size.map(Number) : [];
  const width = dimensions?.width ?? (raw.width as number | undefined) ?? legacySize[0] ?? tiles[0]?.length ?? 25;
  const height = dimensions?.height ?? (raw.height as number | undefined) ?? legacySize[1] ?? tiles.length ?? 25;

  const coverPoints = readPointList(raw.covers);
  const obstaclePoints = readPointList(raw.obstacles);
  for (const [x, y] of coverPoints) {
    if (tiles[y]?.[x] !== undefined) tiles[y][x] = 1;
  }
  for (const [x, y] of obstaclePoints) {
    if (tiles[y]?.[x] !== undefined) tiles[y][x] = 2;
  }
  if (tiles.length !== height || tiles.some((row) => row.length !== width)) {
    throw new Error(`[level-store] 地图网格尺寸无效: expected ${width}x${height}`);
  }

  const spawns = raw.spawns as {
    team_a?: Array<{ x: number; y: number }>;
    team_b?: Array<{ x: number; y: number }>;
  } | undefined;
  const legacySpawnPoints = raw.spawn_points as Record<string, unknown> | undefined;
  const spawnsA = spawns?.team_a?.map((spawn) => [spawn.x, spawn.y] as [number, number])
    ?? readPointList(legacySpawnPoints?.A);
  const spawnsB = spawns?.team_b?.map((spawn) => [spawn.x, spawn.y] as [number, number])
    ?? readPointList(legacySpawnPoints?.B);
  const spawnA = (raw.spawnA as [number, number] | undefined) ?? spawnsA[0] ?? [0, 0];
  const spawnB = (raw.spawnB as [number, number] | undefined) ?? spawnsB[0] ?? [width - 1, 0];

  return {
    width,
    height,
    tiles,
    spawnA,
    spawnB,
    constraints: raw.constraints as MapConstraints | undefined,
    ...(spawnsA.length > 0 ? { spawnsA } : {}),
    ...(spawnsB.length > 0 ? { spawnsB } : {}),
  } as MapData;
}

function readPointList(value: unknown): Array<[number, number]> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((point) => {
    if (Array.isArray(point) && point.length >= 2) {
      const x = Number(point[0]);
      const y = Number(point[1]);
      return Number.isFinite(x) && Number.isFinite(y) ? [[x, y] as [number, number]] : [];
    }
    if (point && typeof point === "object") {
      const candidate = point as { x?: unknown; y?: unknown };
      const x = Number(candidate.x);
      const y = Number(candidate.y);
      return Number.isFinite(x) && Number.isFinite(y) ? [[x, y] as [number, number]] : [];
    }
    return [];
  });
}

export function manifestToLevelMeta(manifest: Record<string, unknown>, isBuiltin: boolean): LevelMeta {
  const metadata = (manifest.metadata as Record<string, unknown> | undefined) ?? {};
  const gameConfig = (manifest.game_config as Record<string, unknown> | undefined) ?? {};
  const dimensions = (metadata.mapDimensions as { width?: number } | undefined);
  const difficulty = (manifest.difficulty ?? metadata.difficulty ?? 'easy') as LevelMeta['difficulty'];
  return {
    levelId: String(manifest.level_id ?? ''),
    name: String(manifest.name ?? metadata.name ?? manifest.level_id ?? ''),
    difficulty,
    mapSize: Number(manifest.mapSize ?? dimensions?.width ?? 25),
    aiName: String(manifest.aiName ?? metadata.aiName ?? 'Unknown'),
    description: String(manifest.description ?? metadata.description ?? ''),
    isBuiltin,
    createdAt: (manifest.createdAt ?? metadata.created_at) as string | undefined,
    thumbnailSrc: manifest.thumbnailSrc as string | undefined,
    difficultyStars: (manifest.difficultyStars ?? metadata.difficultyStars) as number | undefined,
    apiVersion: manifest.apiVersion as string | undefined,
    maxTicks: (gameConfig.max_ticks as number | undefined),
    characterId: gameConfig.character_id as string | undefined,
    tags: metadata.tags as string[] | undefined,
    version: manifest.version as string | undefined,
    maxUnitsPerTeam: gameConfig.max_units_per_team as number | undefined,
  };
}

export class StaticBuiltinLevelStore implements LevelStore {
  private readonly rootUrl = `${import.meta.env.BASE_URL}data/levels/_builtin`.replace(/\/$/, '');
  private indexPromise: Promise<BuiltinLevelIndex> | null = null;
  private metadataPromise: Promise<LevelMeta[]> | null = null;

  private async getIndex(): Promise<BuiltinLevelIndex> {
    if (!this.indexPromise) {
      this.indexPromise = fetch(`${this.rootUrl}/index.json`).then(async (response) => {
        if (!response.ok) throw new Error(`Failed to load builtin level index: HTTP ${response.status}`);
        const index = await response.json() as BuiltinLevelIndex;
        if (!Array.isArray(index.levels)) throw new Error('Invalid builtin level index: levels must be an array');
        return index;
      });
    }
    return this.indexPromise;
  }

  private async getEntry(levelId: string): Promise<BuiltinLevelIndexEntry> {
    const entry = (await this.getIndex()).levels.find((item) => item.levelId === levelId);
    if (!entry) throw new Error(`Builtin level not found: ${levelId}`);
    return entry;
  }

  private async readJson<T>(url: string): Promise<T> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to load ${url}: HTTP ${response.status}`);
    return response.json() as Promise<T>;
  }

  async list(): Promise<LevelMeta[]> {
    if (!this.metadataPromise) {
      this.metadataPromise = (async () => {
        const index = await this.getIndex();
        const result: LevelMeta[] = [];
        for (const entry of index.levels) {
          try {
            const manifest = await this.readJson<Record<string, unknown>>(`${this.rootUrl}/${entry.path}/manifest.json`);
            result.push(manifestToLevelMeta(manifest, true));
          } catch (error) {
            console.warn(`[level-store] builtin manifest failed: ${entry.levelId}`, error);
          }
        }
        return result;
      })();
    }
    try {
      return await this.metadataPromise;
    } catch (error) {
      this.metadataPromise = null;
      throw error;
    }
  }

  async load(levelId: string): Promise<LevelData> {
    const entry = await this.getEntry(levelId);
    const base = `${this.rootUrl}/${entry.path}`;
    const manifest = await this.readJson<Record<string, unknown>>(`${base}/manifest.json`);
    const rawMap = await this.readJson<Record<string, unknown>>(`${base}/map.json`);
    const aiResponse = await fetch(`${base}/ai.py`);
    if (!aiResponse.ok) throw new Error(`Failed to load builtin AI: HTTP ${aiResponse.status}`);
    const meta = manifestToLevelMeta(manifest, true);
    return {
      ...meta,
      map: normalizeMap(rawMap),
      playerTemplate: '',
      aiCode: await aiResponse.text(),
    };
  }

  async save(_level: LevelData): Promise<void> { throw new Error('Builtin levels are read-only'); }
  async delete(_levelId: string): Promise<void> { throw new Error('Builtin levels are read-only'); }
  async exportZip(levelId: string): Promise<Blob> { return packLevelZip(await this.load(levelId)); }
  async importZip(_zipBlob: Blob): Promise<LevelData> { throw new Error('Builtin store cannot import levels'); }
}

// ============================================================
// DevLevelStore — Vite dev server middleware 后端
// ============================================================

/**
 * 开发环境存储实现。
 * 所有操作通过 fetch 调用 Vite middleware（/api/levels）。
 * playerTemplate 不通过此 store 存取（玩家代码走独立 commit 存储）。
 */
export class DevLevelStore implements LevelStore {
  private baseUrl = '/api/levels';

  async save(level: LevelData): Promise<void> {
    const body = {
      manifest: {
        level_id: level.levelId,
        name: level.name,
        difficulty: level.difficulty,
        mapSize: level.mapSize,
        aiName: level.aiName,
        description: level.description,
        isBuiltin: level.isBuiltin,
        createdAt: level.createdAt,
        thumbnailSrc: level.thumbnailSrc,
        // A4: round-trip 保留新字段
        difficultyStars: level.difficultyStars,
        apiVersion: level.apiVersion,
        maxTicks: level.maxTicks,
        characterId: level.characterId,
        tags: level.tags,
        version: level.version,
      },
      map: level.map,
      aiCode: level.aiCode,
    };

    const res = await fetch(`${this.baseUrl}/${encodeURIComponent(level.levelId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.message ?? `Failed to save level: ${level.levelId}`);
    }
  }

  async load(levelId: string): Promise<LevelData> {
    const res = await fetch(`${this.baseUrl}/${encodeURIComponent(levelId)}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.message ?? `Level not found: ${levelId}`);
    }
    const data = await res.json();
    return this.hydrateLevelData(data);
  }

  async list(): Promise<LevelMeta[]> {
    const res = await fetch(this.baseUrl);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.message ?? 'Failed to list levels');
    }
    return res.json();
  }

  async delete(levelId: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/${encodeURIComponent(levelId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.message ?? `Failed to delete level: ${levelId}`);
    }
  }

  async exportZip(levelId: string): Promise<Blob> {
    const level = await this.load(levelId);
    return packLevelZip(level);
  }

  async importZip(zipBlob: Blob): Promise<LevelData> {
    const level = await unpackLevelZip(zipBlob);
    // 导入的关卡重新分配 ID
    level.levelId = `imported_${Date.now().toString(36)}`;
    level.isBuiltin = false;
    await this.save(level);
    return level;
  }

  /** 将 middleware 返回的 JSON 转为 LevelData */
  private hydrateLevelData(data: Record<string, unknown>): LevelData {
    const manifest = (data.manifest as Record<string, unknown>) ?? {};
    const rawMap = (data.map as Record<string, unknown>) ?? {};
    const map: MapData = {
      width: (rawMap.width as number) ?? 25,
      height: (rawMap.height as number) ?? 25,
      tiles: (rawMap.tiles as number[][]) ?? [],
      spawnA: (rawMap.spawnA as [number, number]) ?? [0, 0],
      spawnB: (rawMap.spawnB as [number, number]) ?? [0, 0],
      // ★ 保留 constraints（V6-011 砺锋台分类用）
      constraints: rawMap.constraints as MapData['constraints'],
    };

    return {
      levelId: (manifest.level_id as string) ?? (data.levelId as string) ?? '',
      name: (manifest.name as string) ?? (data.name as string) ?? '',
      difficulty: (manifest.difficulty as LevelData['difficulty']) ?? 'easy',
      mapSize: (manifest.mapSize as number) ?? map.width ?? 25,
      aiName: (manifest.aiName as string) ?? (data.aiName as string) ?? '',
      description: (manifest.description as string) ?? (data.description as string) ?? '',
      isBuiltin: (manifest.isBuiltin as boolean) ?? (data.isBuiltin as boolean) ?? false,
      createdAt: (manifest.createdAt as string) ?? (data.createdAt as string),
      thumbnailSrc: (manifest.thumbnailSrc as string) ?? (data.thumbnailSrc as string),
      // A4: round-trip 保留新字段
      difficultyStars: manifest.difficultyStars as number | undefined,
      apiVersion: manifest.apiVersion as string | undefined,
      maxTicks: manifest.maxTicks as number | undefined,
      characterId: manifest.characterId as string | undefined,
      tags: manifest.tags as string[] | undefined,
      version: manifest.version as string | undefined,
      map,
      playerTemplate: (data.playerTemplate as string) ?? '',
      aiCode: (data.aiCode as string) ?? '',
    };
  }
}

// ============================================================
// OPFSLevelStore — 浏览器 Origin Private File System
// ============================================================

/**
 * V14 兼容迁移源。
 * 仅读取 V14 之前已登录账号在 OPFS 的旧关卡；新关卡不再写入 OPFS。
 */
export class OPFSLevelStore implements LevelStore {
  private root: FileSystemDirectoryHandle | null = null;
  private levelsDir: FileSystemDirectoryHandle | null = null;
  private namespace: string | null = null;

  /** 获取当前账号/匿名设备命名空间下的 levels/ 子目录 */
  private async getLevelsDir(): Promise<FileSystemDirectoryHandle> {
    const namespace = getLevelStorageNamespace();
    if (this.levelsDir && this.namespace === namespace) return this.levelsDir;

    if (!this.root) {
      this.root = await navigator.storage.getDirectory();
    }

    const usersDir = await this.root.getDirectoryHandle('users', { create: true });
    const userDir = await usersDir.getDirectoryHandle(namespace, { create: true });
    this.levelsDir = await userDir.getDirectoryHandle('levels', { create: true });
    this.namespace = namespace;
    return this.levelsDir;
  }

  /** 检查 OPFS 是否可用 */
  static isSupported(): boolean {
    try {
      return typeof navigator !== 'undefined'
        && typeof navigator.storage !== 'undefined'
        && typeof navigator.storage.getDirectory === 'function';
    } catch {
      return false;
    }
  }

  async save(level: LevelData): Promise<void> {
    const levelsDir = await this.getLevelsDir();
    const levelDir = await levelsDir.getDirectoryHandle(level.levelId, { create: true });

    // manifest.json
    const manifest = {
      level_id: level.levelId,
      name: level.name,
      difficulty: level.difficulty,
      mapSize: level.mapSize,
      aiName: level.aiName,
      description: level.description,
      isBuiltin: level.isBuiltin,
      createdAt: level.createdAt ?? new Date().toISOString(),
      thumbnailSrc: level.thumbnailSrc,
      // A4: round-trip 保留新字段
      difficultyStars: level.difficultyStars,
      apiVersion: level.apiVersion,
      maxTicks: level.maxTicks,
      characterId: level.characterId,
      tags: level.tags,
      version: level.version,
    };
    await this.writeFile(levelDir, 'manifest.json', JSON.stringify(manifest, null, 2));

    // map.json
    await this.writeFile(levelDir, 'map.json', JSON.stringify(level.map, null, 2));

    // ai.py
    await this.writeFile(levelDir, 'ai.py', level.aiCode);
  }

  async load(levelId: string): Promise<LevelData> {
    const levelsDir = await this.getLevelsDir();
    let levelDir: FileSystemDirectoryHandle;
    try {
      levelDir = await levelsDir.getDirectoryHandle(levelId);
    } catch {
      throw new Error(`Level not found in OPFS: ${levelId}`);
    }

    const manifestRaw = await this.readFile(levelDir, 'manifest.json');
    const manifest = JSON.parse(manifestRaw);
    const mapRaw = await this.readFile(levelDir, 'map.json');
    const map = JSON.parse(mapRaw);
    const aiCode = await this.readFile(levelDir, 'ai.py');

    return {
      levelId: manifest.level_id ?? levelId,
      name: manifest.name ?? '',
      difficulty: manifest.difficulty ?? 'easy',
      mapSize: manifest.mapSize ?? map.width ?? 25,
      aiName: manifest.aiName ?? '',
      description: manifest.description ?? '',
      isBuiltin: manifest.isBuiltin ?? false,
      createdAt: manifest.createdAt,
      thumbnailSrc: manifest.thumbnailSrc,
      // A4: round-trip 保留新字段
      difficultyStars: manifest.difficultyStars,
      apiVersion: manifest.apiVersion,
      maxTicks: manifest.maxTicks,
      characterId: manifest.characterId,
      tags: manifest.tags,
      version: manifest.version,
      map,
      playerTemplate: '',  // 玩家代码不存 OPFS
      aiCode,
    };
  }

  async list(): Promise<LevelMeta[]> {
    const levelsDir = await this.getLevelsDir();
    const result: LevelMeta[] = [];

    // OPFS 的 FileSystemDirectoryHandle 支持 entries() 异步迭代
    for await (const [name, handle] of (levelsDir as unknown as AsyncIterable<[string, FileSystemDirectoryHandle]>)) {
      if (handle.kind === 'directory') {
        try {
          const manifestRaw = await this.readFile(handle, 'manifest.json');
          const manifest = JSON.parse(manifestRaw);
          result.push({
            levelId: manifest.level_id ?? name,
            name: manifest.name ?? name,
            difficulty: manifest.difficulty ?? 'easy',
            mapSize: manifest.mapSize ?? 25,
            aiName: manifest.aiName ?? '',
            description: manifest.description ?? '',
            isBuiltin: manifest.isBuiltin ?? false,
            createdAt: manifest.createdAt,
            thumbnailSrc: manifest.thumbnailSrc,
            // A4: round-trip 保留新字段
            difficultyStars: manifest.difficultyStars,
            apiVersion: manifest.apiVersion,
            maxTicks: manifest.maxTicks,
            characterId: manifest.characterId,
            tags: manifest.tags,
            version: manifest.version,
          });
        } catch {
          // 跳过无效关卡目录（缺少 manifest.json）
        }
      }
    }

    return result;
  }

  async delete(levelId: string): Promise<void> {
    const levelsDir = await this.getLevelsDir();
    try {
      await levelsDir.removeEntry(levelId, { recursive: true });
    } catch (e) {
      throw new Error(`Failed to delete level '${levelId}': ${(e as Error).message}`);
    }
  }

  async exportZip(levelId: string): Promise<Blob> {
    const level = await this.load(levelId);
    return packLevelZip(level);
  }

  async importZip(zipBlob: Blob): Promise<LevelData> {
    if (!isLoggedIn()) throw new AuthRequiredError();
    const level = await unpackLevelZip(zipBlob);
    level.levelId = `imported_${Date.now().toString(36)}`;
    level.isBuiltin = false;
    await this.save(level);
    return level;
  }

  /** 写文本文件到 OPFS 目录 */
  private async writeFile(dir: FileSystemDirectoryHandle, name: string, content: string): Promise<void> {
    const fh = await dir.getFileHandle(name, { create: true });
    const writable = await fh.createWritable();
    await writable.write(content);
    await writable.close();
  }

  /** 从 OPFS 目录读文本文件 */
  private async readFile(dir: FileSystemDirectoryHandle, name: string): Promise<string> {
    const fh = await dir.getFileHandle(name);
    const file = await fh.getFile();
    return file.text();
  }
}

// ============================================================
// AccountUserLevelStore — 账号云端用户关卡
// ============================================================

export class AccountUserLevelStore implements LevelStore {
  private readonly baseUrl = '/api/user/levels';
  private readonly legacyOpfs = new OPFSLevelStore();

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const token = getAuthToken();
    if (!token) throw new AuthRequiredError();
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const apiBase = import.meta.env.DEV ? 'http://localhost:3001' : '';
    const response = await fetch(`${apiBase}${this.baseUrl}${path}`, { ...init, headers });
    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(String(body.message ?? `用户关卡请求失败: HTTP ${response.status}`));
    }
    return response;
  }

  async list(): Promise<LevelMeta[]> {
    const response = await this.request('');
    const body = await response.json() as { levels?: Record<string, unknown>[] };
    const remoteLevels = (body.levels ?? []).map((manifest) => manifestToLevelMeta({
      ...manifest,
      level_id: manifest.level_id ?? manifest.levelId,
    }, false));

    // V14 之前账号关卡可能仍在 OPFS；登录后一次性迁移到云端，成功后删除本地副本。
    if (OPFSLevelStore.isSupported()) {
      const remoteIds = new Set(remoteLevels.map((level) => level.levelId));
      const legacyLevels = await this.legacyOpfs.list();
      for (const legacyMeta of legacyLevels) {
        if (remoteIds.has(legacyMeta.levelId)) {
          await this.legacyOpfs.delete(legacyMeta.levelId);
          continue;
        }
        const legacyLevel = await this.legacyOpfs.load(legacyMeta.levelId);
        await this.save(legacyLevel);
        await this.legacyOpfs.delete(legacyMeta.levelId);
        remoteLevels.push({ ...legacyMeta, isBuiltin: false });
      }
    }
    return remoteLevels;
  }

  async load(levelId: string): Promise<LevelData> {
    const response = await this.request(`/${encodeURIComponent(levelId)}`);
    const body = await response.json() as { level?: Record<string, unknown> };
    const raw = body.level;
    if (!raw) throw new Error(`用户关卡响应缺少数据: ${levelId}`);
    const manifest = (raw.manifest as Record<string, unknown>) ?? {};
    const rawMap = (raw.map as Record<string, unknown>) ?? {};
    return {
      ...manifestToLevelMeta({ ...manifest, level_id: manifest.level_id ?? levelId }, false),
      map: normalizeMap(rawMap),
      playerTemplate: '',
      aiCode: typeof raw.aiCode === 'string' ? raw.aiCode : '',
    };
  }

  async save(level: LevelData): Promise<void> {
    await this.request(`/${encodeURIComponent(level.levelId)}`, {
      method: 'PUT',
      body: JSON.stringify({
        manifest: {
          level_id: level.levelId,
          name: level.name,
          difficulty: level.difficulty,
          mapSize: level.mapSize,
          aiName: level.aiName,
          description: level.description,
          isBuiltin: false,
          createdAt: level.createdAt,
          thumbnailSrc: level.thumbnailSrc,
          difficultyStars: level.difficultyStars,
          apiVersion: level.apiVersion,
          maxTicks: level.maxTicks,
          characterId: level.characterId,
          tags: level.tags,
          version: level.version,
        },
        map: level.map,
        aiCode: level.aiCode,
      }),
    });
  }

  async delete(levelId: string): Promise<void> {
    await this.request(`/${encodeURIComponent(levelId)}`, { method: 'DELETE' });
  }

  async exportZip(levelId: string): Promise<Blob> {
    return packLevelZip(await this.load(levelId));
  }

  async importZip(zipBlob: Blob): Promise<LevelData> {
    if (!isLoggedIn()) throw new AuthRequiredError();
    const level = await unpackLevelZip(zipBlob);
    level.levelId = `imported_${Date.now().toString(36)}`;
    level.isBuiltin = false;
    await this.save(level);
    return level;
  }
}

// ============================================================
// CompositeLevelStore — 官方静态内容 + 账号云端内容
// ============================================================

export class CompositeLevelStore implements LevelStore {
  constructor(
    private readonly builtin: StaticBuiltinLevelStore,
    private readonly user: AccountUserLevelStore,
  ) {}

  async list(): Promise<LevelMeta[]> {
    const builtinLevels = await this.builtin.list();
    if (!isLoggedIn()) return builtinLevels;
    const userLevels = await this.user.list();
    const builtinIds = new Set(builtinLevels.map((level) => level.levelId));
    const filteredUserLevels = userLevels.filter((level) => !builtinIds.has(level.levelId));
    return [...builtinLevels, ...filteredUserLevels];
  }

  async load(levelId: string): Promise<LevelData> {
    const builtinIds = new Set((await this.builtin.list()).map((item) => item.levelId));
    if (builtinIds.has(levelId)) {
      // 官方 ID 永远以静态内容为准，资源损坏时必须显式报错。
      return this.builtin.load(levelId);
    }
    if (!isLoggedIn()) throw new AuthRequiredError("请先登录后再加载用户关卡");
    return this.user.load(levelId);
  }

  async save(level: LevelData): Promise<void> {
    if (level.isBuiltin) throw new Error('Builtin levels are read-only; fork before saving');
    if (!isLoggedIn()) throw new AuthRequiredError();
    const builtinIds = new Set((await this.builtin.list()).map((item) => item.levelId));
    if (builtinIds.has(level.levelId)) throw new Error(`Level ID conflicts with builtin level: ${level.levelId}`);
    await this.user.save(level);
  }

  async delete(levelId: string): Promise<void> {
    if (!isLoggedIn()) throw new AuthRequiredError();
    const builtinIds = new Set((await this.builtin.list()).map((level) => level.levelId));
    if (builtinIds.has(levelId)) throw new Error('Builtin levels are read-only');
    await this.user.delete(levelId);
  }

  async exportZip(levelId: string): Promise<Blob> {
    return packLevelZip(await this.load(levelId));
  }

  async importZip(zipBlob: Blob): Promise<LevelData> {
    if (!isLoggedIn()) throw new AuthRequiredError();
    const level = await unpackLevelZip(zipBlob);
    level.levelId = `imported_${Date.now().toString(36)}`;
    level.isBuiltin = false;
    await this.save(level);
    return level;
  }
}

// ============================================================
// 工厂函数：环境自动选择
// ============================================================

let _store: LevelStore | null = null;

/**
 * 创建或获取 LevelStore 单例。
 *
 * 环境判断逻辑：
 * - import.meta.env.DEV === true → DevLevelStore（fetch → Vite middleware）
 * - 生产环境 → OPFSLevelStore（浏览器 OPFS）
 * - OPFS 不可用 → 抛错（提示用户升级浏览器）
 */
export function createLevelStore(): LevelStore {
  if (_store) return _store;

  // Vite 在 dev 模式下设置 import.meta.env.DEV = true
  if (import.meta.env.DEV) {
    _store = new DevLevelStore();
    return _store;
  }

  // 生产环境：官方关卡走静态资源；用户关卡走账号 API，服务端为 canonical source。
  const builtinStore = new StaticBuiltinLevelStore();
  _store = new CompositeLevelStore(builtinStore, new AccountUserLevelStore());
  return _store;
}

/**
 * 获取 LevelStore 单例（便捷方法，与 createLevelStore 等价）。
 * 首次调用时自动根据环境选择实现，后续调用返回同一实例。
 */
export function getLevelStore(): LevelStore {
  return createLevelStore();
}
